import { defineStore } from 'pinia'
import type { CityDataFromDetail } from '~~/interfaces/city'
import type { Statistics } from '~~/interfaces/network'
import type { Network, PoolFromMappy } from '#cmui/types'

const useCityStore = defineStore('city', () => {
    const baseUrl = tryUseNuxtApp()
        ? useRuntimeConfig().public.APPLICATION_URL
        : process.env.NUXT_PUBLIC_APPLICATION_URL

    const slug = ref('')
    const countryCode = ref('')
    const cityData = ref<CityDataFromDetail | null>(null)
    const cityStatistics = ref<Statistics | null>(null)
    const pools = ref<PoolFromMappy[]>([])
    const totalPools = ref(0)
    const poolsLoading = ref(false)
    const pageIndex = ref(1)
    const cachedDataCityDetailDate = ref('')
    const cachedDataCityStatisticsDate = ref('')

    const name = computed<string>(() => cityData.value?.name || '')
    const countryId = computed<number>(() => cityData.value?.countryId || 0)

    const initialCenterLat = computed<number>(
        () => cityData.value?.initialCenterLatitude || 0
    )

    const initialCenterLng = computed<number>(
        () => cityData.value?.initialCenterLongitude || 0
    )

    const checkinsCount = computed<number>(
        () => cityStatistics.value?.checkinsCount || 0
    )

    const stationsTotalCount = computed<number>(
        () => cityData.value?.stationsTotalCount || 0
    )

    const stationsCompatibleChargemapPassCount = computed<number>(
        () => cityData.value?.stationsCompatibleChargemapPassCount || 0
    )

    const stationsFastChargeCount = computed<number>(
        () => cityData.value?.stationsFastChargeCount || 0
    )

    const stationsFreeCount = computed<number>(
        () => cityData.value?.stationsFreeCount || 0
    )

    const topNetworks = computed<Network[]>(
        () => cityData.value?.networksWithTheMostStations || []
    )

    async function fetchCityData() {
        if (slug.value && countryCode.value) {
            try {
                ;[cityData.value, cachedDataCityDetailDate.value] =
                    await $fetch<[CityDataFromDetail, string]>(
                        `${baseUrl}/api/cities/detail`,
                        {
                            query: {
                                slug: slug.value,
                                countryCode: countryCode.value
                            }
                        }
                    )
            } catch (error: any) {
                if (error.status !== 404) {
                    useNuxtApp().$throwError(error, 'FetchCityData')
                }

                return showError({
                    statusCode: error.status,
                    statusMessage: error.statusText
                })
            }
        }
    }

    async function fetchCityStatistics() {
        if (slug.value && countryCode.value) {
            try {
                ;[cityStatistics.value, cachedDataCityStatisticsDate.value] =
                    await $fetch<[Statistics, string]>(
                        `${baseUrl}/api/cities/statistics`,
                        {
                            query: {
                                slug: slug.value,
                                countryCode: countryCode.value
                            }
                        }
                    )
            } catch (error: any) {
                if (error.status !== 404) {
                    useNuxtApp().$throwError(error, 'FetchCityStatistics')
                }

                return showError({
                    statusCode: error.status,
                    statusMessage: error.statusText
                })
            }
        }
    }

    async function fetchPoolsInCity(offset: number, limit: number) {
        if (slug.value && countryId.value) {
            try {
                poolsLoading.value = true

                const returnedObject: {
                    total: number
                    pools: PoolFromMappy[]
                } = await $fetch(`${baseUrl}/api/cities/pools`, {
                    query: {
                        citySlug: slug.value,
                        countryId: countryId.value,
                        offset,
                        limit
                    }
                })

                totalPools.value = returnedObject.total
                pools.value = returnedObject.pools
                poolsLoading.value = false
            } catch (error: any) {
                poolsLoading.value = false

                if (error.status !== 404) {
                    useNuxtApp().$throwError(error, 'FetchPoolsInCity')
                }

                return showError({
                    statusCode: error.status,
                    statusMessage: error.statusText
                })
            }
        }
    }

    return {
        slug,
        name,
        countryCode,
        countryId,
        pools,
        poolsLoading,
        pageIndex,
        totalPools,
        initialCenterLat,
        initialCenterLng,
        cityData,
        cityStatistics,
        checkinsCount,
        stationsTotalCount,
        stationsCompatibleChargemapPassCount,
        stationsFastChargeCount,
        stationsFreeCount,
        topNetworks,
        cachedDataCityDetailDate,
        cachedDataCityStatisticsDate,
        fetchCityData,
        fetchCityStatistics,
        fetchPoolsInCity
    }
})

type CityStore = Omit<
    ReturnType<typeof useCityStore>,
    keyof ReturnType<typeof defineStore>
>

export default useCityStore

export type { CityStore }
